import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchPackages } from '@/lib/api';
import { Calendar, Check, Star, Crown, Gem } from 'lucide-react';
import SEO from '@/components/SEO';

const PKG_ICONS = [Star, Crown, Gem];

export default function Parties() {
  const [packages, setPackages] = useState([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchPackages().then(setPackages).catch(() => {}); }, []);

  return (
    <div data-testid="parties-page" className="pt-[68px]">
      <SEO title="Birthday Parties & Events" description="All-inclusive sports birthday party packages starting at $159.99. Dodgeball, soccer, hockey and more — book your party!" path="/parties" />
      <section className="relative py-24 bg-[#0B1D3A] overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(ellipse 600px 400px at 60% 40%, rgba(242,101,34,0.25) 0%, transparent 70%)'
        }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">Birthday Parties & Events</h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">The ultimate sports birthday experience. Action-packed parties that kids will never forget!</p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {packages.map((pkg, i) => {
              const Icon = PKG_ICONS[i] || Star;
              return (
                <div
                  key={pkg.id}
                  data-testid={`party-package-${pkg.id}`}
                  className={`rounded-2xl border-2 p-8 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl ${
                    i === 1
                      ? 'bg-[#0B1D3A] border-[#F26522]/40 lg:-mt-4 ring-1 ring-[#F26522]/20'
                      : 'bg-white border-gray-200 hover:border-[#F26522]/30'
                  }`}
                >
                  {i === 1 && (
                    <div className="bg-[#F26522] text-white text-xs font-semibold px-3 py-1 rounded-full inline-block mb-4">MOST POPULAR</div>
                  )}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${i === 1 ? 'bg-[#F26522]/20' : 'bg-[#F26522]/10'}`}>
                    <Icon size={24} className="text-[#F26522]" />
                  </div>
                  <h3 className={`text-2xl font-bold mb-1 ${i === 1 ? 'text-white' : 'text-[#0B1D3A]'}`}>{pkg.name}</h3>
                  <div className="text-4xl font-bold text-[#F26522]  mb-2">{pkg.price}</div>
                  <div className={`text-sm mb-6 ${i === 1 ? 'text-gray-400' : 'text-gray-500'}`}>{pkg.duration} &middot; {pkg.min_guests}-{pkg.max_guests} guests</div>

                  <div className="mb-4">
                    <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${i === 1 ? 'text-gray-400' : 'text-gray-400'}`}>Sports Included</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {pkg.sports_included?.map(s => (
                        <span key={s} className="bg-[#F26522]/10 text-[#F26522] px-2.5 py-1 rounded-full text-xs font-medium">{s}</span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${i === 1 ? 'text-gray-400' : 'text-gray-400'}`}>What's Included</h4>
                    <div className="space-y-2">
                      {pkg.whats_included?.map((item, j) => (
                        <div key={j} className={`flex items-start gap-2 text-sm ${i === 1 ? 'text-gray-300' : 'text-gray-600'}`}>
                          <Check size={14} className="text-[#F26522] mt-0.5 shrink-0" />
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>

                  <a
                    href={pkg.booking_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`book-party-${pkg.id}`}
                    className={`block w-full text-center py-3.5 rounded-lg font-semibold text-sm transition-all ${
                      i === 1
                        ? 'bg-[#F26522] hover:bg-[#D9551A] text-white shadow-lg shadow-orange-500/20'
                        : 'bg-[#0B1D3A] hover:bg-[#162D4D] text-white'
                    }`}
                  >
                    <Calendar size={14} className="inline mr-2" />
                    Book This Package
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-[#0B1D3A] mb-4">Have Questions?</h2>
          <p className="text-gray-500 mb-6">Contact us to customize your party or discuss special requirements</p>
          <Link to="/contact" className="inline-flex items-center gap-2 border-2 border-[#F26522] text-[#F26522] hover:bg-[#F26522] hover:text-white px-8 py-3 rounded-lg font-semibold transition-all">
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
}
